// Web MIDI *output* sink (mirror of MidiInput). Sends note-on/off + panic CCs to the
// keyboard's own synth so the player hears the notes they don't play (F1) — no in-app
// synth. Reuses the shared MIDIAccess; tracks its own sounding set so panic is coherent
// even if a note-off was missed. No clock: it's driven once per RAF frame by PlaybackOut.
import { CONFIG } from "../config";
import { isLoopbackPort, type MidiPort } from "./input";

const NOTE_ON = 0x90;
const NOTE_OFF = 0x80;
const CONTROL_CHANGE = 0xb0;

/** A minimal output port — note send + the panic CCs. */
export interface MidiSink {
  noteOn(midi: number, velocity: number): void;
  noteOff(midi: number): void;
  panic(): void;
}

/**
 * Auto-pair the output to the selected input by name (one bidirectional device — the
 * WP01 — is both). Prefer an exact name match, else the first non-loopback output, else
 * none. Pure, mirrors `pickAutoDevice`.
 */
export function pickAutoOutput(outputs: MidiPort[], inputName: string | null): string | null {
  const real = outputs.filter((p) => !isLoopbackPort(p.name));
  if (inputName) {
    const exact = real.find((p) => p.name === inputName);
    if (exact) return exact.id;
  }
  return real[0]?.id ?? null;
}

const clampVel = (velocity: number): number =>
  Math.max(CONFIG.midiOut.velFloor, Math.min(127, Math.round(velocity * 127)));

export class MidiOutput implements MidiSink {
  private access: MIDIAccess | null = null;
  private current: MIDIOutput | null = null;
  /** Pitches this sink currently has sounding, so panic clears exactly them. */
  private readonly on = new Set<number>();
  /** Capped log of *intended* sends (recorded even with no port) — an E2E seam so a
   *  test can assert the out-stream headlessly. Not used by the app. */
  private readonly recent: string[] = [];
  private record(op: string): void {
    this.recent.push(op);
    if (this.recent.length > 64) this.recent.shift();
  }
  /** The recent intended-send log (`on:60`, `off:60`, `panic`), for E2E. */
  recentOut(): string[] {
    return [...this.recent];
  }

  /** Adopt the shared access from MidiInput (no second requestMIDIAccess). */
  useAccess(access: MIDIAccess | null): void {
    this.access = access;
  }

  /** Connected output ports. */
  ports(): MidiPort[] {
    if (!this.access) return [];
    return [...this.access.outputs.values()].map((p) => ({ id: p.id, name: p.name ?? p.id }));
  }

  /** Select an output by id (panics any prior port first). `null` deselects. */
  select(id: string | null): void {
    if (this.current) this.panic();
    this.current = id && this.access ? (this.access.outputs.get(id) ?? null) : null;
  }

  private send(bytes: number[]): void {
    this.current?.send(bytes);
  }

  noteOn(midi: number, velocity: number): void {
    this.record(`on:${midi}`);
    if (!this.current) return;
    this.send([NOTE_ON | CONFIG.midiOut.channel, midi & 0x7f, clampVel(velocity)]);
    this.on.add(midi);
  }

  noteOff(midi: number): void {
    this.record(`off:${midi}`);
    if (!this.current) return;
    this.send([NOTE_OFF | CONFIG.midiOut.channel, midi & 0x7f, 0]);
    this.on.delete(midi);
  }

  /** Silence everything: explicit note-offs for the tracked set + all-notes/sound-off
   *  CCs (belt and braces), so a dropped note-off can never hang. */
  panic(): void {
    this.record("panic");
    if (!this.current) {
      this.on.clear();
      return;
    }
    for (const midi of this.on) this.send([NOTE_OFF | CONFIG.midiOut.channel, midi & 0x7f, 0]);
    this.on.clear();
    this.send([CONTROL_CHANGE | CONFIG.midiOut.channel, CONFIG.midiOut.allNotesOffCc, 0]);
    this.send([CONTROL_CHANGE | CONFIG.midiOut.channel, CONFIG.midiOut.allSoundOffCc, 0]);
  }
}
